import { useMemo, useState } from 'react';
import { Modal } from '../../components/Modal';
import { MoneyInput } from '../../components/inputs/MoneyInput';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { formatMoeda, formatMargem, parseMoeda } from '@fiodecobre/shared';
import { BotaoEstimativaCusto } from '../../components/EstimativaCusto';

// Produto enxuto que o modal precisa pra montar o preview.
export interface ProdutoPreco {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  preco_custo: string;
  preco_pix: string;
}

type Modo = 'fixo' | 'percentual' | 'margem';

interface Props {
  produtos: ProdutoPreco[];
  onFechar: () => void;
  onConcluido: () => void;
}

const r4 = (v: number) => Math.round(v * 10000) / 10000;
const arred10 = (v: number) => Math.round(v * 10) / 10;

// Valor com que o campo do modo 'fixo' ABRE: o que todos os selecionados tem
// quando batem, ou o MAIS FREQUENTE quando divergem (empate: o maior).
// O caso de uso e' justamente emparelhar uma familia que desandou de preco entre
// compras, entao zerar o campo na divergencia so devolvia o retrabalho; quem
// mostra o que vai ser sobrescrito e' a tabela de previa, linha a linha.
function valorDePartida(vals: number[]): { valor: number; uniforme: boolean } {
  const contagem = new Map<number, number>();
  for (const v of vals) if (Number.isFinite(v)) contagem.set(v, (contagem.get(v) ?? 0) + 1);
  let valor = 0;
  let melhor = -1;
  for (const [v, n] of contagem) {
    if (n > melhor || (n === melhor && v > valor)) { valor = v; melhor = n; }
  }
  return { valor, uniforme: contagem.size <= 1 };
}

interface LinhaPreview {
  id: number;
  codigo: string;
  descricao: string;
  custoAtual: number;
  custoNovo: number;
  pixAtual: number;
  pixNovo: number;
  tocouCusto: boolean;
  tocouPix: boolean;
}

const MODOS: { valor: Modo; titulo: string; desc: string }[] = [
  { valor: 'fixo', titulo: 'Valor fixo', desc: 'Define o mesmo custo / preço pra todos os produtos marcados.' },
  { valor: 'percentual', titulo: 'Reajuste percentual', desc: 'Aplica um % sobre o valor atual de cada produto (+ sobe, − desce).' },
  { valor: 'margem', titulo: 'Manter margem', desc: 'Você muda o custo e a venda acompanha, preservando a margem de cada um.' },
];

export function ModalPrecosLote({ produtos, onFechar, onConcluido }: Props) {
  const toast = useToast();
  const [modo, setModo] = useState<Modo>('fixo');
  const [salvando, setSalvando] = useState(false);

  // modo 'fixo'
  // Ambos marcados por default; valor 0 conta como "nao alterar" (guarda > 0).
  // Os campos ABREM com o preco que os selecionados ja tem, quando todos tem o
  // mesmo (pedido do Ruan, 29/08/2026) — o caso comum e' subir o preco de uma
  // familia inteira, e digitar do zero um valor que ja esta na tela e' retrabalho.
  const custoPartida = useMemo(() => valorDePartida(produtos.map((p) => Number(p.preco_custo))), [produtos]);
  const pixPartida = useMemo(() => valorDePartida(produtos.map((p) => Number(p.preco_pix))), [produtos]);
  const [aplicarCusto, setAplicarCusto] = useState(true);
  const [custoFixo, setCustoFixo] = useState(custoPartida.valor);
  const [aplicarVenda, setAplicarVenda] = useState(true);
  const [vendaFixa, setVendaFixa] = useState(pixPartida.valor);

  // modo 'percentual'
  const [aplicarPctCusto, setAplicarPctCusto] = useState(true);
  const [pctCusto, setPctCusto] = useState('');
  const [aplicarPctVenda, setAplicarPctVenda] = useState(true);
  const [pctVenda, setPctVenda] = useState('');

  // modo 'margem'
  const [margemTipo, setMargemTipo] = useState<'fixo' | 'percentual'>('percentual');
  const [margemPct, setMargemPct] = useState('');
  const [margemCustoFixo, setMargemCustoFixo] = useState(0);

  const [arredondar, setArredondar] = useState(true);

  const arredAtivo = modo !== 'fixo' && arredondar;

  const divergem = [custoPartida.uniforme ? null : 'custo', pixPartida.uniforme ? null : 'preço de venda']
    .filter(Boolean)
    .join(' e o ');

  const preview = useMemo<LinhaPreview[]>(() => {
    return produtos.map((p) => {
      const custoAtual = Number(p.preco_custo);
      const pixAtual = Number(p.preco_pix);
      let custoNovo = custoAtual;
      let pixNovo = pixAtual;
      let tocouCusto = false;
      let tocouPix = false;

      if (modo === 'fixo') {
        // Valor 0 = "nao alterar" — a coluna segue mostrando o preco atual.
        if (aplicarCusto && custoFixo > 0) { custoNovo = custoFixo; tocouCusto = true; }
        if (aplicarVenda && vendaFixa > 0) { pixNovo = vendaFixa; tocouPix = true; }
      } else if (modo === 'percentual') {
        if (aplicarPctCusto) { custoNovo = custoAtual * (1 + parseMoeda(pctCusto) / 100); tocouCusto = true; }
        if (aplicarPctVenda) { pixNovo = pixAtual * (1 + parseMoeda(pctVenda) / 100); tocouPix = true; }
      } else {
        custoNovo = margemTipo === 'fixo' ? margemCustoFixo : custoAtual * (1 + parseMoeda(margemPct) / 100);
        tocouCusto = true;
        if (custoAtual > 0) { pixNovo = pixAtual * (custoNovo / custoAtual); tocouPix = true; }
      }

      if (custoNovo < 0) custoNovo = 0;
      if (pixNovo < 0) pixNovo = 0;
      if (tocouPix && arredAtivo) pixNovo = arred10(pixNovo);

      return {
        id: p.id,
        codigo: p.codigo_interno ?? '—',
        descricao: p.descricao,
        custoAtual,
        custoNovo: r4(custoNovo),
        pixAtual,
        pixNovo: r4(pixNovo),
        tocouCusto,
        tocouPix,
      };
    });
  }, [produtos, modo, aplicarCusto, custoFixo, aplicarVenda, vendaFixa, aplicarPctCusto, pctCusto, aplicarPctVenda, pctVenda, margemTipo, margemPct, margemCustoFixo, arredAtivo]);

  // Com os campos ja preenchidos, "tocou" nao basta pra liberar o botao: abrir e
  // clicar gravaria o MESMO valor e o aviso diria "6 produto(s) com preco
  // atualizado" sem nada ter mudado. So conta linha que muda de valor de verdade.
  const semAlteracao = !preview.some(
    (l) => (l.tocouCusto && l.custoNovo !== l.custoAtual) || (l.tocouPix && l.pixNovo !== l.pixAtual),
  );
  const prejuizo = preview.filter((l) => l.pixNovo > 0 && l.pixNovo < l.custoNovo).length;

  const valido = (() => {
    if (modo === 'fixo') return (aplicarCusto && custoFixo > 0) || (aplicarVenda && vendaFixa > 0);
    if (modo === 'percentual') {
      return (aplicarPctCusto && pctCusto.trim() !== '') || (aplicarPctVenda && pctVenda.trim() !== '');
    }
    return margemTipo === 'fixo' ? margemCustoFixo > 0 : margemPct.trim() !== '';
  })();

  async function confirmar() {
    if (!valido || salvando) return;
    const body: Record<string, unknown> = { produto_ids: produtos.map((p) => p.id), modo };
    if (modo === 'fixo') {
      if (aplicarCusto && custoFixo > 0) body.preco_custo = custoFixo;
      if (aplicarVenda && vendaFixa > 0) body.preco_pix = vendaFixa;
    } else if (modo === 'percentual') {
      if (aplicarPctCusto) body.pct_custo = parseMoeda(pctCusto);
      if (aplicarPctVenda) body.pct_venda = parseMoeda(pctVenda);
      body.arredondar = arredondar;
    } else {
      body.margem_tipo = margemTipo;
      body.margem_valor = margemTipo === 'fixo' ? margemCustoFixo : parseMoeda(margemPct);
      body.arredondar = arredondar;
    }
    setSalvando(true);
    try {
      const r = await api.patch<{ atualizados: number }>('/api/produtos/precos-lote', body);
      toast.sucesso(`${r.atualizados} produto(s) com preço atualizado.`);
      onConcluido();
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  const inputPct = (valor: string, set: (v: string) => void, disabled: boolean) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <input
        type="text"
        inputMode="decimal"
        className="border border-gray-300 rounded-lg text-sm px-2 py-1.5"
        style={{ width: 90, textAlign: 'right' }}
        value={valor}
        disabled={disabled}
        placeholder="0"
        onChange={(e) => set(e.target.value)}
      />
      <span className="muted">%</span>
    </div>
  );

  return (
    <Modal aberto onFechar={onFechar} titulo="Alterar preços em lote" largura={760}>
      <p style={{ marginTop: 0, fontSize: 14 }}>
        <strong>{produtos.length}</strong> produto(s) selecionado(s). O preço no cartão recalcula sozinho a partir do PIX.
      </p>

      {/* Seletor de modo */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {MODOS.map((m) => (
          <button
            key={m.valor}
            type="button"
            onClick={() => setModo(m.valor)}
            style={{
              flex: '1 1 220px',
              textAlign: 'left',
              padding: '10px 12px',
              borderRadius: 8,
              cursor: 'pointer',
              border: modo === m.valor ? '2px solid var(--primary)' : '1px solid var(--border, #d1d5db)',
              background: modo === m.valor ? 'var(--panel-bg)' : 'transparent',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14 }}>{m.titulo}</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{m.desc}</div>
          </button>
        ))}
      </div>

      {/* Campos por modo */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
        {modo === 'fixo' && (
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={aplicarCusto} onChange={(e) => setAplicarCusto(e.target.checked)} />
                <span style={{ width: 170 }}>Definir novo custo</span>
                <MoneyInput valor={custoFixo} onChangeValor={setCustoFixo} casas={4} disabled={!aplicarCusto} className="border border-gray-300 rounded-lg text-sm px-2 py-1.5" style={{ width: 130, textAlign: 'right' }} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={aplicarVenda} onChange={(e) => setAplicarVenda(e.target.checked)} />
                <span style={{ width: 170 }}>Definir novo preço de venda</span>
                <MoneyInput valor={vendaFixa} onChangeValor={setVendaFixa} casas={4} disabled={!aplicarVenda} className="border border-gray-300 rounded-lg text-sm px-2 py-1.5" style={{ width: 130, textAlign: 'right' }} />
              </label>
            </div>
            <BotaoEstimativaCusto onUsar={(c) => { setCustoFixo(c); setAplicarCusto(true); }} />
          </div>
        )}

        {modo === 'fixo' && divergem !== '' && (
          <p className="muted" style={{ fontSize: 12, margin: 0 }}>
            Os campos abrem com o valor que os selecionados já têm. O {divergem} varia entre eles — veio o mais frequente, e a tabela abaixo mostra linha a linha o que muda.
          </p>
        )}

        {modo === 'percentual' && (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={aplicarPctCusto} onChange={(e) => setAplicarPctCusto(e.target.checked)} />
              <span style={{ width: 170 }}>Reajustar custo em</span>
              {inputPct(pctCusto, setPctCusto, !aplicarPctCusto)}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={aplicarPctVenda} onChange={(e) => setAplicarPctVenda(e.target.checked)} />
              <span style={{ width: 170 }}>Reajustar venda em</span>
              {inputPct(pctVenda, setPctVenda, !aplicarPctVenda)}
            </label>
          </>
        )}

        {modo === 'margem' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ width: 170 }}>O custo vai virar</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" checked={margemTipo === 'percentual'} onChange={() => setMargemTipo('percentual')} />
                um reajuste %
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" checked={margemTipo === 'fixo'} onChange={() => setMargemTipo('fixo')} />
                um valor fixo
              </label>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 170 }}>{margemTipo === 'fixo' ? 'Novo custo (R$)' : 'Reajuste do custo'}</span>
              {margemTipo === 'fixo'
                ? <MoneyInput valor={margemCustoFixo} onChangeValor={setMargemCustoFixo} casas={4} className="border border-gray-300 rounded-lg text-sm px-2 py-1.5" style={{ width: 130, textAlign: 'right' }} />
                : inputPct(margemPct, setMargemPct, false)}
            </label>
            <p className="muted" style={{ fontSize: 12, margin: 0 }}>
              A venda de cada produto sobe/desce na mesma proporção, mantendo a margem atual.
            </p>
          </>
        )}

        {modo !== 'fixo' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={arredondar} onChange={(e) => setArredondar(e.target.checked)} />
            Arredondar preço de venda para múltiplos de R$ 0,10
          </label>
        )}
      </div>

      {prejuizo > 0 && (
        <div style={{ background: '#fde68a', color: '#000', padding: '6px 10px', borderRadius: 6, fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
          Atenção: {prejuizo} produto(s) ficariam com preço de venda ABAIXO do custo.
        </div>
      )}

      {/* Preview */}
      <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border, #e5e7eb)', borderRadius: 6 }}>
        <table className="tabela" style={{ margin: 0 }}>
          <thead>
            <tr>
              <th style={{ width: 64 }}>COD.</th>
              <th>DESCRIÇÃO</th>
              <th style={{ textAlign: 'right' }}>CUSTO</th>
              <th style={{ textAlign: 'right' }}>VENDA (PIX)</th>
              <th style={{ textAlign: 'right' }}>MARGEM</th>
            </tr>
          </thead>
          <tbody>
            {preview.map((l) => {
              const prej = l.pixNovo > 0 && l.pixNovo < l.custoNovo;
              return (
                <tr key={l.id} style={prej ? { background: '#fee2e2' } : undefined}>
                  <td><span className="codigo-curto">{l.codigo}</span></td>
                  <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.descricao}</td>
                  <td style={{ textAlign: 'right' }}>
                    {l.tocouCusto && l.custoNovo !== l.custoAtual
                      ? <span><span className="muted">{formatMoeda(l.custoAtual, 4)}</span> → <strong>{formatMoeda(l.custoNovo, 4)}</strong></span>
                      : formatMoeda(l.custoAtual, 4)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {l.tocouPix && l.pixNovo !== l.pixAtual
                      ? <span><span className="muted">{formatMoeda(l.pixAtual, 4)}</span> → <strong>{formatMoeda(l.pixNovo, 4)}</strong></span>
                      : formatMoeda(l.pixAtual, 4)}
                  </td>
                  <td style={{ textAlign: 'right' }}>{formatMargem(l.pixNovo, l.custoNovo)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button type="button" className="btn-secundario" onClick={onFechar} disabled={salvando}>Cancelar</button>
        <button type="button" className="btn-primario" onClick={confirmar} disabled={!valido || semAlteracao || salvando}>
          {salvando ? 'Aplicando...' : `Aplicar a ${produtos.length} produto(s)`}
        </button>
      </div>
    </Modal>
  );
}
